import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { storeUsernameRef } from "@/lib/referral";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";

/**
 * Лендинг приглашения: тёплое приветствие от пригласившего.
 * Username сохраняем в localStorage — при регистрации он резолвится в user_id
 * и записывается в profiles.referred_by (см. applyStoredReferral в __root).
 */
export const Route = createFileRoute("/$username/invite")({
  ssr: false,
  component: InvitePage,
});

type Inviter = { full_name: string; photo_url: string | null; city: string | null };

function InvitePage() {
  const { username } = Route.useParams();

  useEffect(() => {
    storeUsernameRef(username);
  }, [username]);

  const { data: inviter } = useQuery({
    queryKey: ["public-inviter", username],
    queryFn: async (): Promise<Inviter | null> => {
      const { data, error } = await supabase.rpc("public_inviter", { _username: username });
      if (error) throw error;
      const row = (data as Inviter[] | null)?.[0];
      return row ?? null;
    },
  });

  const firstName = (inviter?.full_name ?? "").split(" ")[0] ?? "";
  const initials = (inviter?.full_name ?? "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md rounded-3xl border border-td-line bg-white p-6 text-center shadow-sm">
        <div className="flex justify-center">
          {inviter?.photo_url ? (
            <img loading="lazy" decoding="async"
              src={inviter.photo_url}
              alt={inviter.full_name}
              className="h-24 w-24 rounded-full object-cover ring-2 ring-td-teal/30"
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-td-teal font-display text-2xl font-semibold text-white">
              {initials}
            </div>
          )}
        </div>

        <h1 className="mt-5 font-display text-xl font-bold text-td-dark">
          🐾 {firstName || "Друг"} приглашает тебя в TurkeyDrive
        </h1>

        <p className="mt-3 text-sm text-td-dark/80">
          TurkeyDrive — сообщество русскоязычных в Турции. Единая афиша событий в твоём городе,
          сообщества по интересам, знакомства и помощь экспертов.
        </p>

        <Link
          to="/auth"
          className="cta-solid mt-6 inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold"
        >
          Присоединиться
        </Link>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-td-muted">
          <MrDriveAvatar variant="home" size={32} />
          <span>MrDrive уже подбирает тебе своих</span>
        </div>
      </div>
    </div>
  );
}
